import React from 'react';
import BigHeader from '../../components/BigHeader';
import Image from 'react-bootstrap/Image'



const Safety = () => {

    return (
        
		<div class="page-body">
            			<BigHeader headerText={'Safety'} backgroundImage={'https://static.igem.wiki/teams/4602/wiki/images/images/background.jpg'}/>
			<div style={{margin: '53px'}}>
			<h1 id="27b9b64c-de0e-400d-8bfd-69c88f2fe9a9" class="">Introduction</h1>
			<p id="0f790f26-1e22-4ddf-8810-f19f8d3ef4ee" class="">Safety has been a keystone in our approach since the
				conception of Lubritect. Since our product is intended to be used as a medical device that will come
				into contact with human skin, it needed to be designed in a way which addresses concerns that usually
				arise with integrating a synthetic biology product in our everyday lives. Inspired by <a
					href="https://2022.igem.wiki/leiden/safety">previous iGEM projects</a>, we specifically chose to
				have a cell-free product to mitigate health concerns around genetically modified organisms (GMOs) such
				as <em>E. coli</em>. Apart from increasing the shelf life, a cell free lubricant is also more user
				friendly and augmentable as compared to GMOs, which also have additional concerns of containment and
				biohazard safety. </p>
			<p id="b67324bc-84a8-4a53-a13d-07b12e38fc86" class="">
			</p>
			<p id="dadc8a1d-82bb-4b64-a183-e5dce45aab24" class="">Our attempt at materializing this product entails
				expressing proteins in <em>E. coli</em>, extracting and purifying them, and testing them on
				bacteriophages. Each of these steps requires certain safety standards and considerations.</p>
			<h2 id="bbd3fd15-e3a5-467b-a6f6-dcbb9b0c4205" class="">Wet Lab Safety</h2>
			<p id="7c61ac23-afec-4b1a-bb4a-0087b6ee8aab" class=""><strong>Genetic designs</strong></p>
			<p id="7f0a975f-4fbe-4564-ba90-d5cec20ce489" class="">The gene fragments and primers we needed were all
				ordered through IDT and Twist Bioscience which screen all gene orders for potentially dangerous
				transcripts, ensuring that we did not synthesize any toxins even by an oversight. Fortunately, none of
				our AI-generated sequences matched any dangerous transcripts within their databases!</p>
			<p id="0785ff61-9bde-4450-87f0-27eb7262ff7e" class=""><strong>Protein expression</strong></p>
			<p id="738b10ae-ec95-4017-b1ca-ad5c93a8e367" class="">All of our experiments were planned and executed with
				organisms from the Risk Group 1 (<em><em><em><em><em><em><em><em><em>E.
													coli</em></em></em></em></em></em></em></em></em> strains DH5-alpha,
				TOP10, Rosetta(DE3), and T4 bacteriophages). No organisms from Risk Group 3 and 4, nor parts from an
				organisms from Group 4, were used. After the expression of our protein, the cells were lysed and
				centrifuged to extract the protein followed by several rounds of purification, this ensured that the
				resulting solution did not contain any bacterial cells and required no further pathogen containment
				measures. </p>
			<p id="cb5a303b-b67a-434c-a20b-aab3ffacec06" class=""><strong>Phage experiments</strong></p>
			<p id="89e70fe5-4e9b-4c2f-9ce8-11c5480e08d6" class="">We have used lytic bacteriophage T4 to demonstrate a
				proof of concept. This phage is non pathogenic to humans and infects only<em> E. coli</em>,
				<em>Shigella</em>, <em>Salmonella</em>, <em>Aerobacter</em>, <em>Proteus</em>, <em>Serratia</em>, 
				and<em> Yersinia </em>[1]. Two of the four wet lab members had prior experience of working with this
				phage and were trained to take the necessary precautions to prevent phage contamination. These were the
				only members of the team who worked with phages through the duration of the project.</p>
			<p id="1c7c1b7f-552e-4b11-9b40-8ad216b2cdf5" class="">
			</p>
			<figure class="block-color-gray_background callout" style={{whiteSpace:"pre-wrap", display: "flex"}}
				id="48377c53-6501-4095-8c20-a9ea360b23ad">
				<div style={{fontSize:"1.5em"}}><span class="icon">💡</span></div>
				<div style={{width:"100%"}}>Training
					Team members who took part in the wet lab have partaken in the biosafety and security training
					organized by our school, led by Hortense Vielfaure who is an INSERM certified trained biosafety
					officer, and the lab manager on the premises. She was present in the lab throughout the week, and
					has been in constant touch with us, monitoring and guiding our work in the lab.</div>
			</figure>
			<p id="d9f13844-621d-4b47-839c-01c1f0693b4f" class="">
			</p>
			<p id="63a3ec28-e375-4703-9e28-d57067dd409c" class=""><strong>Safety Inventory</strong></p>
			<p id="d39d4aef-21cd-4f37-9e73-0e15798901a5" class="">Our laboratory is equipped with common safety and
				protective equipment, and can be accessed only by the authorized personnel. We have access to a
				biosafety cabinet, laminar flow hood, chemical fume hood, emergency eyewash station, fire extinguishers,
				first aid kits, and emergency showers. Lab coats and protective nitrile gloves are also provided by the
				lab. All the members working in the lab also had previous experience with laboratories and were familiar
				with the usage of all safety devices.</p>
			<p id="83104511-65d6-4be7-9f47-4ba49e50dff5" class="">
			</p>
			<Image  fluid src={"https://static.igem.wiki/teams/4602/wiki/lab-safety-examples/laminar-flow-hood.jpg"}></Image>
			<Image  fluid src={"https://static.igem.wiki/teams/4602/wiki/lab-safety-examples/chemical-fume-hood.jpg"}></Image>
			<h2 id="b0fbc938-9197-4e49-8c9e-956f4a629ec5" class="">Product Safety</h2>
			<p id="175afac7-63b1-4fb5-9d55-841d75c5a744" class="">On top of a cell free approach, we took further
				precautions while creating the proof of concept. Since our product is meant to target sexually
				transmitted pathogens in humans, apart from the T4 bacteriophage, we used HPV as a model organism
				without incurring the risks that come with working with the virus. To do this, HPV capsid proteins were
				used for the experiments, as we aimed to show that our RFdiffusion generated proteins specifically bound
				to them. The individual HPV capsid proteins may assemble into virus-like particles (VLPs), but this
				assembly is non-pathogenic [2]. However these virus-like particles can incite immunogenic response [3].
				To address this problem, all of the experiments concerning HPV were performed under a laminar flow hood,
				where the proteins were always in a cell free solution after purification and there was no direct
				contact with skin.</p>
			<h2 id="c0e89de9-29b2-41b9-ba5b-220895d7c8d3" class="">Future Directions</h2>
			<p id="56378fd3-bf52-4d71-a202-5a19c32dfe30" class="">To expand upon our current progress and maintain our
				commitment to safety we plan to design immunological assays as soon as we have the first prototype.
				These assays can be performed on reconstructed human vaginal and rectal epithelium in a laboratory that
				has the facilities that allow experimentation with human cells, as suggested by Dr Morgane Bomsel.
				Beyond this lie feasibility studies and clinical trials of our product. A detailed outline of our
				approach to clinical trials is provided in the <em>(link to </em><em>entrepreneurial part, clinical
					trials</em><em>).</em> </p>
			<p id="bb584c09-b8eb-477e-9492-4fa7c3931739" class="">We have undertaken an extensive effort to being
				informed about our product safety by <a
					href="https://2023.igem.wiki/paris-bettencourt/human-practices">medical practitioners and public
					health experts </a><em>(link human practices&gt; integrated human practice&gt;Public Health
					Research)</em>. Safety is not just restricted to our product itself! We hope to kindle and nurture
				conversations around safe sex. We aspire to co-create a product that takes into consideration sexual
				health in diverse socio-economic contexts and provides an experience that makes the user feel more
				secure and comfortable.</p>
			<p id="b3baf78e-ab47-4931-8c18-01aabf06c301" class="">Surpassing this, we aim to design a user-friendly
				packaging which would provide the best shelf life and least chance of contamination. Accompanied by a
				detailed user manual in a transparent and accessible language, it would outline the backing science,
				possible misuses, and tips on storage and discarding (for more information, see Product Design). Our
				product is meant to be used by everyone!</p>
			<p id="83c91249-416d-4aaf-8e16-ba548a26703d" class="">
			</p>
			<h2 id="eef438da-9f93-44ea-90a0-c017c9adc808" class="">References</h2>
			<p id="ef046985-34f1-4bf6-8f84-8b9f45df1f03" class="">[1] Washizaki, A., Yonesaki, T., &amp; Otsuka, Y.
				(2016). Characterization of the interactions between Escherichia coli receptors, LPS and OmpC, and
				bacteriophage T4 long tail fibers. <em>Microbiologyopen</em>, <em>5</em>(6), 1003-1015.</p>
			<p id="72e2fea1-16d9-4c4f-89c2-9db791bef99c" class="">[2] Kirnbauer, R., Taub, J., Greenstone, H., Roden,
				R., Dürst, M., Gissmann, L., Lowy, D. R., &amp; Schiller, J. T. (1993). Efficient self-assembly of human
				papillomavirus type 16 L1 and L1-L2 into virus-like particles. <em>Journal of Virology</em>, 67(12)
				6929–6936. doi: <a
					href="https://doi.org/10.1128%2Fjvi.67.12.6929-6936.1993">10.1128/jvi.67.12.6929-6936.1993</a></p>
			<p id="e3915743-9117-4a5a-bc3b-c99cf0cfc05a" class="">[3] Kirnbauer, R., Booy, F., Cheng, N., Lowy, D. R.,
				&amp; Schiller, J. T. (1992). Papillomavirus L1 major capsid protein self-assembles into virus-like
				particles that are highly immunogenic. <em>Proceedings of the National Academy of Sciences of the United
					States of America</em>, <em>89</em>(24), 12180–12184.</p>
			<p id="ed61bc47-2ec3-4554-acc9-45f49269a1fa" class="">
			</p>
			</div>
        </div>
    )
}
export default Safety;
